import React from 'react'
import ContentWrapper from './ContentWrapper'
import { Box } from '@mui/material'
import Txt from './Txt'
import TextField from './TextField'
import SelectField from './SelectField'
import Button from './Button'
import CheckBox from './CheckBox'
import { handleMutationError } from '../utils/errors'

export type DialogConfigItem = {
	property: string
	type: 'select' | 'number' | 'price' | 'checkbox'
	options?: (string | { label: string; value: string | number })[]
	allowEmpty?: boolean
	allowZero?: boolean
	label: string
}

type FieldProps = {
	property: string
	label: string
	value: unknown
	onChange: (value: unknown, property?: string) => void
}

// Ein Preis als zwei Felder, Euro und Cent; im Formular steht er in Cent.
const PriceField = React.memo(({ property, label, value, onChange }: FieldProps): JSX.Element => {
	const cents = typeof value === 'number' && Number.isFinite(value) ? value : null
	const euroPart = cents === null ? '' : Math.floor(cents / 100).toString()
	const centPart = cents === null ? '' : (cents % 100).toString().padStart(2, '0')

	const handleChange = React.useCallback(
		(nextEuros: string, nextCents: string) => {
			const sanitizedEuros = nextEuros.replace(/\D/g, '')
			const sanitizedCents = nextCents.replace(/\D/g, '')
			onChange(
				sanitizedEuros === '' && sanitizedCents === ''
					? null
					: Number(sanitizedEuros || 0) * 100 + Math.min(99, Number(sanitizedCents || 0)),
				property
			)
		},
		[onChange, property]
	)
	const handleEurosChange = React.useCallback(
		(nextEuros: unknown) => handleChange(String(nextEuros), centPart),
		[centPart, handleChange]
	)
	const handleCentsChange = React.useCallback(
		(nextCents: unknown) => handleChange(euroPart, String(nextCents)),
		[euroPart, handleChange]
	)

	return (
		<Box m="1rem 0">
			<Txt variant="body2" m="0 0 1rem 0">
				{label}
			</Txt>
			<Box display="flex" flexDirection="row" gap="1rem">
				<TextField
					label="Preis €"
					defaultValue={euroPart}
					id={`${property}-euros`}
					onChange={handleEurosChange}
					type="text"
					onlyDigits
				/>
				<TextField
					label="Preis Cent"
					defaultValue={centPart}
					id={`${property}-cents`}
					onChange={handleCentsChange}
					type="text"
					onlyDigits
					min={0}
					max={99}
				/>
			</Box>
		</Box>
	)
})
PriceField.displayName = 'PriceField'

const ConfigSelectField = React.memo(
	({
		property,
		label,
		options,
		onChange,
	}: Omit<FieldProps, 'value'> & { options: DialogConfigItem['options'] }): JSX.Element => {
		const items = React.useMemo(
			() =>
				(options ?? []).map((option) =>
					typeof option === 'string'
						? { label: option, value: option }
						: { label: option.label, value: String(option.value) }
				),
			[options]
		)
		const handleChange = React.useCallback(
			(value: string | null) => onChange(value, property),
			[onChange, property]
		)
		return <SelectField label={label} items={items} m="1rem" onChange={handleChange} />
	}
)
ConfigSelectField.displayName = 'ConfigSelectField'

// Formular-Dialog für Gemeinden und Lieferslots: Felder aus `config`, gespeichert über `onSubmit`.
// Gibt onSubmit false zurück, bleibt der Dialog offen.
const CreateDialog = ({
	title,
	config,
	onSubmit,
	dismissPortal,
	defaultValues,
}: {
	title: string
	config: DialogConfigItem[]
	onSubmit: (args: { data: Record<string, unknown> }) => Promise<boolean>
	dismissPortal: () => void
	defaultValues?: Record<string, string | number | boolean | undefined>
}): JSX.Element => {
	const [loading, setLoading] = React.useState(false)
	const [formState, setFormState] = React.useState<Record<string, unknown>>(() => ({
		...defaultValues,
	}))

	const handleChange = React.useCallback((value: unknown, id?: string) => {
		if (!id) return
		setFormState((prev) => ({ ...prev, [id]: value }))
	}, [])

	const handleSave = React.useCallback(async () => {
		const invalidPrice = config
			.filter((item) => item.type === 'price')
			.some((item) => {
				const cents = formState[item.property]
				return typeof cents !== 'number'
					? !item.allowEmpty
					: cents < 0 || (!item.allowZero && cents === 0)
			})
		if (invalidPrice) {
			handleMutationError('create', 'Preis muss größer als 0 sein')
		} else {
			setLoading(true)
			const saved = await onSubmit({ data: formState })
			setLoading(false)
			if (saved) {
				dismissPortal()
			}
		}
	}, [config, dismissPortal, formState, onSubmit])

	return (
		<ContentWrapper>
			<Box display="flex" flexDirection="column" width="30rem" p="1rem 2rem 2rem 2rem">
				<Box display="flex" flexDirection="row" p="1rem" justifyContent="center">
					<Txt variant="h4">{title}</Txt>
				</Box>
				{config.map(({ property, type, options, label }) =>
					type === 'checkbox' ? (
						<Box key={property} m="0.5rem 0 1rem 0">
							<CheckBox
								label={label}
								defaultValue={formState[property] === true}
								id={property}
								onChange={handleChange}
							/>
						</Box>
					) : type === 'price' ? (
						<PriceField
							key={property}
							property={property}
							label={label}
							value={formState[property]}
							onChange={handleChange}
						/>
					) : type === 'number' ? (
						<TextField
							key={property}
							label={label}
							m="1rem 0"
							defaultValue={formState[property] as number}
							id={property}
							onChange={handleChange}
							type="number"
						/>
					) : (
						<ConfigSelectField
							key={property}
							property={property}
							label={label}
							options={options}
							onChange={handleChange}
						/>
					)
				)}

				<Box display="flex" flexDirection="row" justifyContent="flex-end" m="1rem 0 0 0">
					<Button variant="outlined" m="0 1rem 0 0" onClick={dismissPortal} loading={loading}>
						Schließen
					</Button>
					<Button variant="outlined" onClick={handleSave} loading={loading}>
						Speichern
					</Button>
				</Box>
			</Box>
		</ContentWrapper>
	)
}

export default CreateDialog
